import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Download, CheckCircle, Phone, Settings, Droplets, Zap, Mail, RotateCw, Clock, AlertTriangle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const VacuumCleaningPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'manual' | 'automatic'>('manual');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    if (location.hash === '#automatic') {
      setActiveTab('automatic');
    }
  }, [location]);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "How much water is lost during vacuum cleaning purge cycles?",
      answer: "Vacuum cleaning is extremely efficient with water usage. During each purge cycle (typically 10-20 seconds), only the debris captured by the vacuum nozzles is backflushed—not the entire filter volume. Water loss ranges from 0.5-2% of total flow depending on cleaning frequency, making vacuum technology ideal for water-scarce regions or applications where minimizing waste is critical."
    },
    {
      question: "What types of solids does vacuum cleaning handle best?",
      answer: "Vacuum cleaning excels with granular, heavy debris including sand, silt, clay particles, and mineral sediments. The powerful suction action effectively removes dense particles that settle on screens. It's particularly effective for fine mesh screens (80-120 mesh) handling high solids loading. For fibrous organic matter or algae that adheres to screens, brush cleaning may be more effective."
    },
    {
      question: "How often do automatic vacuum (AVC) systems clean, and can I adjust the timing?",
      answer: "AVC systems trigger cleaning based on differential pressure (ΔP) setpoint or timer intervals—both are fully adjustable. Typical ΔP setpoints range from 5-15 PSI. Timer intervals can be set from minutes to hours based on water quality. Many installations use hybrid control: timer-based during peak debris periods plus ΔP backup for unexpected loads. Cleaning cycles complete in 10-20 seconds."
    },
    {
      question: "What maintenance is required for vacuum cleaning systems?",
      answer: "Vacuum systems require minimal routine maintenance. Quarterly: inspect vacuum nozzles and scanner arm for wear or debris buildup. Annually: replace head gaskets and inspect screens. The vacuum scanner has few moving parts and typically lasts 5-10 years. For AVC systems, verify ΔP sensor calibration annually. Clean water applications require less frequent service than heavy debris loads."
    },
    {
      question: "Can I retrofit from manual (MVC) to automatic (AVC)?",
      answer: "Yes! The BIG Series platform supports field retrofits from MVC to AVC. The automatic vacuum controller and motor mount directly to the existing filter body—no repiping or welding required. You'll need to add 120V or 240V power and install the differential pressure sensor. The mechanical vacuum scanner remains the same. Contact your dealer for AVC retrofit kit availability."
    },
    {
      question: "What mesh sizes work best with vacuum cleaning?",
      answer: "Vacuum cleaning works exceptionally well across a wide mesh range—from coarse 20 mesh to fine 120 mesh. The powerful suction handles fine mesh screens better than brush cleaning, making it ideal for drip irrigation (80-120 mesh) with heavy solids loading. Vacuum technology is the preferred choice when you need fine filtration combined with high debris capacity and minimal water loss."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="BIG Vacuum Cleaning Filters | MVC (Manual) & AVC (Automatic) | CTC Waterworks"
        description="Vacuum cleaning technology page with full specs, models/kits, and parts. Manual MVC and automatic AVC options for BIG Series inline filters handling granular and heavy solids."
        canonical="https://www.ctcwaterworks.com/filtration/inline/big/vacuum-cleaning"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/inline/big" className="text-gray-600 hover:text-ctc-blue">BIG Series</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Vacuum Cleaning</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-orange-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Droplets className="text-orange-600 mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      BIG—Vacuum Cleaning Technology
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  Manual (MVC) and Automatic (AVC) vacuum filters for granular/heavy solids.
                </p>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-orange-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Expert
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Vacuum Cleaning Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-orange-600 p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-orange-600 underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Images Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/MVC.jpeg" alt="MVC Manual Vacuum Cycle Product" className="w-full h-auto" />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/MVC Internal.jpeg" alt="Vacuum Internal Components" className="w-full h-auto" style={{maxHeight: '400px', objectFit: 'cover'}} />
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-300 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Decades of Proven Performance</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The MVC/AVC vacuum cleaning system represents decades of field-proven technology. Our vacuum scanner design has been refined over generations of agricultural use, delivering reliable performance in the harshest environments.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  From early manual systems to today's automated controllers, the core vacuum technology remains a testament to robust engineering and practical design.
                </p>
              </div>
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src="/MVC Legacy.jpeg" alt="MVC Legacy System Vintage Photo" className="w-full h-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Shared Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12 flex justify-center">
            <img src="/MVC Diagram.jpeg" alt="MVC Technical Diagram" className="max-w-full h-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-br from-orange-50 to-white border-2 border-orange-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Droplets className="mr-3 text-orange-600" size={28} />
                Vacuum Scanner Action
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Internal vacuum scanner arm with indexed nozzles sweeps the screen interior during cleaning cycles. Powerful suction created by differential pressure extracts debris through rapid backflush. The scanner systematically covers the entire screen surface for thorough cleaning.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Vacuum nozzles sweep screen interior</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">High suction extracts heavy debris</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-orange-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">10-20 second cleaning cycles</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Zap className="mr-3 text-blue-600" size={28} />
                Control Options
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Manual (MVC):</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Operator initiates cleaning cycle via hand crank or manual valve. Scanner sweeps screen and purges debris. Simple mechanical operation—ideal for accessible sites with predictable cleaning schedules.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Automatic (AVC):</h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    Differential pressure (ΔP) sensor or timer automatically triggers motor-driven cleaning cycles. Set ΔP threshold (5-15 PSI) or time interval for completely hands-free operation at remote sites.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Best For:</span> Wells, rivers, and canals with sand, silt, clay, and heavy granular solids. Vacuum technology excels with high debris loading, fine mesh screens (80-120 mesh), and applications requiring minimal water loss during cleaning cycles.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg shadow-lg p-2 inline-flex">
              <button
                onClick={() => setActiveTab('manual')}
                className={`px-8 py-4 rounded-lg font-semibold transition-colors ${
                  activeTab === 'manual'
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Manual (MVC)
              </button>
              <button
                onClick={() => setActiveTab('automatic')}
                id="automatic"
                className={`px-8 py-4 rounded-lg font-semibold transition-colors ${
                  activeTab === 'automatic'
                    ? 'bg-orange-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Automatic (AVC)
              </button>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'manual' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Manual Vacuum Cleaning (MVC)</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  MVC (Manual Vacuum Cleaning) systems use operator-initiated cleaning cycles. Turn the hand crank or open the manual valve to activate the vacuum scanner—it sweeps the screen interior and purges debris through rapid backflush. Ideal for accessible sites where periodic manual maintenance fits operational workflow.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Operator Control</h4>
                    <p className="text-sm text-gray-700">Manual initiation—clean when you need it</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Rapid Cycles</h4>
                    <p className="text-sm text-gray-700">10-20 second cleaning, minimal downtime</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Low Water Loss</h4>
                    <p className="text-sm text-gray-700">Efficient purge—only debris backflushed</p>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">MVC & AVC Model Matrix</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full border-collapse bg-white">
                    <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Size</th>
                        <th className="px-4 py-3 text-left font-semibold">Manual Model (MVC)</th>
                        <th className="px-4 py-3 text-left font-semibold">Auto Model (AVC)</th>
                        <th className="px-4 py-3 text-left font-semibold">Screen Part #</th>
                        <th className="px-4 py-3 text-left font-semibold">Flow (gpm)</th>
                        <th className="px-4 py-3 text-left font-semibold">Screen Length</th>
                        <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                        <th className="px-4 py-3 text-left font-semibold">Perf</th>
                        <th className="px-4 py-3 text-left font-semibold">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">4"</td>
                        <td className="px-4 py-3 text-sm">BA611</td>
                        <td className="px-4 py-3 text-sm">BA594</td>
                        <td className="px-4 py-3 text-sm">A4052</td>
                        <td className="px-4 py-3 text-sm">500</td>
                        <td className="px-4 py-3 text-sm">24"</td>
                        <td className="px-4 py-3 text-sm">80</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Small systems</td>
                      </tr>
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">6"</td>
                        <td className="px-4 py-3 text-sm">BA610</td>
                        <td className="px-4 py-3 text-sm">BA602</td>
                        <td className="px-4 py-3 text-sm">A4080</td>
                        <td className="px-4 py-3 text-sm">800</td>
                        <td className="px-4 py-3 text-sm">36"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Mid-size</td>
                      </tr>
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">8"</td>
                        <td className="px-4 py-3 text-sm">BA609</td>
                        <td className="px-4 py-3 text-sm">BA604</td>
                        <td className="px-4 py-3 text-sm">B4103</td>
                        <td className="px-4 py-3 text-sm">1000</td>
                        <td className="px-4 py-3 text-sm">48"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Common model</td>
                      </tr>
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">10S</td>
                        <td className="px-4 py-3 text-sm">BA608</td>
                        <td className="px-4 py-3 text-sm">BA605</td>
                        <td className="px-4 py-3 text-sm">B4033S</td>
                        <td className="px-4 py-3 text-sm">1500</td>
                        <td className="px-4 py-3 text-sm">60"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">High load</td>
                      </tr>
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">10M</td>
                        <td className="px-4 py-3 text-sm">BA597</td>
                        <td className="px-4 py-3 text-sm">BA680</td>
                        <td className="px-4 py-3 text-sm">B5032</td>
                        <td className="px-4 py-3 text-sm">2000</td>
                        <td className="px-4 py-3 text-sm">90"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Heavy-duty</td>
                      </tr>
                      <tr className="hover:bg-orange-50">
                        <td className="px-4 py-3 font-semibold">10L</td>
                        <td className="px-4 py-3 text-sm">BA596</td>
                        <td className="px-4 py-3 text-sm">BA675</td>
                        <td className="px-4 py-3 text-sm">B5025</td>
                        <td className="px-4 py-3 text-sm">3200</td>
                        <td className="px-4 py-3 text-sm">120"</td>
                        <td className="px-4 py-3 text-sm">60</td>
                        <td className="px-4 py-3 text-sm">.032</td>
                        <td className="px-4 py-3 text-sm">Jumbo</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-blue-50 border-l-4 border-orange-600 p-4 rounded-r-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Note:</span> All MVC models include complete filter assembly with head, body, manual vacuum scanner mechanism, purge valve, and one standard screen. Custom sizes and configurations available—contact engineering for details.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'automatic' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Automatic Vacuum Cleaning (AVC)</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  AVC (Automatic Vacuum Cleaning) systems use electric motor drive with differential pressure sensors or timers to automatically initiate cleaning cycles. The vacuum scanner activates on demand—no operator required. Perfect for remote installations, 24/7 operations, or any application requiring unattended maintenance.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Fully Automatic</h4>
                    <p className="text-sm text-gray-700">ΔP sensor or timer-triggered cleaning</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Maximum Uptime</h4>
                    <p className="text-sm text-gray-700">Continuous operation, no shutdowns</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Minimal Water Loss</h4>
                    <p className="text-sm text-gray-700">0.5-2% flow loss during purge cycles</p>
                  </div>
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-blue-50 border-l-4 border-orange-600 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Power Requirements</h4>
                    <p className="text-sm text-gray-700">
                      Standard 120V or 240V AC power. Motor activates only during 10-20 second cleaning cycles. Minimal energy consumption. Integrates with SCADA and irrigation controllers.
                    </p>
                  </div>
                  <div className="bg-blue-50 border-l-4 border-orange-600 p-4 rounded-r-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Cycle Control</h4>
                    <p className="text-sm text-gray-700">
                      Choose ΔP-based (adjustable 5-15 PSI setpoint), timer-based (minutes to hours), or hybrid mode combining both. Fully adjustable to match your water quality and flow patterns.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Shared Spec Table */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">BIG Series Vacuum Cleaning Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Common specifications for both manual (MVC) and automatic (AVC) vacuum cleaning systems. Dimensions and flow rates are identical—only the control mechanism differs.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Model</th>
                  <th className="px-4 py-3 text-left font-semibold">Size (nominal)</th>
                  <th className="px-4 py-3 text-left font-semibold">Connection</th>
                  <th className="px-4 py-3 text-left font-semibold">Flow Range (gpm)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Length (in)</th>
                  <th className="px-4 py-3 text-left font-semibold">Standard Mesh/Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Approx. Weight (lb)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">MVC/AVC-2</td>
                  <td className="px-4 py-3">2"</td>
                  <td className="px-4 py-3">2" NPT</td>
                  <td className="px-4 py-3">20-80</td>
                  <td className="px-4 py-3">18</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">50</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">MVC/AVC-3</td>
                  <td className="px-4 py-3">3"</td>
                  <td className="px-4 py-3">3" NPT</td>
                  <td className="px-4 py-3">50-150</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">85</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">MVC/AVC-4</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">4" NPT</td>
                  <td className="px-4 py-3">100-300</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">135</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">MVC/AVC-6</td>
                  <td className="px-4 py-3">6"</td>
                  <td className="px-4 py-3">6" Flange</td>
                  <td className="px-4 py-3">300-700</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">275</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-4 py-3 font-semibold">MVC/AVC-8</td>
                  <td className="px-4 py-3">8"</td>
                  <td className="px-4 py-3">8" Flange</td>
                  <td className="px-4 py-3">500-1200</td>
                  <td className="px-4 py-3">42</td>
                  <td className="px-4 py-3">20-120 mesh</td>
                  <td className="px-4 py-3">425</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-orange-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Important:</span> Flow ranges are guidelines for typical installations. Actual capacity depends on screen mesh size, solids loading, and acceptable head loss. Vacuum systems handle higher debris loads than brush systems. Contact engineering for application-specific sizing. Custom sizes beyond 8" available.
            </p>
          </div>
        </div>
      </section>

      {/* Replacement Parts Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Replacement Parts & Service Components</h2>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Size</th>
                    <th className="px-4 py-3 text-left font-semibold">Body</th>
                    <th className="px-4 py-3 text-left font-semibold">Head</th>
                    <th className="px-4 py-3 text-left font-semibold">Inlet</th>
                    <th className="px-4 py-3 text-left font-semibold">O-ring</th>
                    <th className="px-4 py-3 text-left font-semibold">Blue Wedge</th>
                    <th className="px-4 py-3 text-left font-semibold">Gasket</th>
                    <th className="px-4 py-3 text-left font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">3NPT</td>
                    <td className="px-4 py-3 text-sm">B4475</td>
                    <td className="px-4 py-3 text-sm">A4472</td>
                    <td className="px-4 py-3 text-sm">B578</td>
                    <td className="px-4 py-3 text-sm">A575</td>
                    <td className="px-4 py-3 text-sm">A174</td>
                    <td className="px-4 py-3 text-sm">—</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">4NPT</td>
                    <td className="px-4 py-3 text-sm">B4527</td>
                    <td className="px-4 py-3 text-sm">A4526</td>
                    <td className="px-4 py-3 text-sm">B612</td>
                    <td className="px-4 py-3 text-sm">A618</td>
                    <td className="px-4 py-3 text-sm">A619</td>
                    <td className="px-4 py-3 text-sm">—</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">6</td>
                    <td className="px-4 py-3 text-sm">B4529</td>
                    <td className="px-4 py-3 text-sm">B4528</td>
                    <td className="px-4 py-3 text-sm">B623</td>
                    <td className="px-4 py-3 text-sm">A620</td>
                    <td className="px-4 py-3 text-sm">A622</td>
                    <td className="px-4 py-3 text-sm">—</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-semibold">8</td>
                    <td className="px-4 py-3 text-sm">B4572</td>
                    <td className="px-4 py-3 text-sm">B4573</td>
                    <td className="px-4 py-3 text-sm">B1060</td>
                    <td className="px-4 py-3 text-sm">A3779</td>
                    <td className="px-4 py-3 text-sm">A1073</td>
                    <td className="px-4 py-3 text-sm">—</td>
                    <td className="px-4 py-3 text-sm">—</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Parts Availability:</span> All replacement parts are stocked by authorized dealers. Vacuum nozzles and scanner arms are field-serviceable. For part numbers, availability, and pricing, contact your local dealer or CTC Waterworks engineering.
            </p>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Quick Guide */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Quick Guide</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Vacuum cleaning excels across a wide mesh range—from coarse to fine. Particularly effective with 80-120 mesh for demanding drip irrigation applications.
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-orange-600 to-orange-700 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Application</th>
                  <th className="px-6 py-4 text-left font-semibold">Recommended Mesh</th>
                  <th className="px-6 py-4 text-left font-semibold">Micron Range</th>
                  <th className="px-6 py-4 text-left font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-orange-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Drip Irrigation (Heavy Solids)</td>
                  <td className="px-6 py-4 text-gray-700">100-120 mesh</td>
                  <td className="px-6 py-4 text-gray-600">125-149 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Vacuum excels with fine mesh + high debris</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Drip Irrigation (Moderate Solids)</td>
                  <td className="px-6 py-4 text-gray-700">80-100 mesh</td>
                  <td className="px-6 py-4 text-gray-600">149-177 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Standard drip emitter protection</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Micro-Sprinkler</td>
                  <td className="px-6 py-4 text-gray-700">50-80 mesh</td>
                  <td className="px-6 py-4 text-gray-600">177-297 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Medium mesh for granular debris</td>
                </tr>
                <tr className="hover:bg-orange-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Intake Protection</td>
                  <td className="px-6 py-4 text-gray-700">20-40 mesh or 1/16" perf</td>
                  <td className="px-6 py-4 text-gray-600">420-840 µm</td>
                  <td className="px-6 py-4 text-sm text-gray-600">Coarse pre-filtration for pump protection</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Vacuum Advantage:</span> Vacuum cleaning handles fine mesh (100-120) with high solids loading better than brush technology. The powerful suction extracts heavy granular debris that would quickly plug fine screens. Ideal when you need maximum filtration combined with heavy debris capacity.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Compare Other Technologies</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/filtration/inline/big/brush-cleaning"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center mb-3">
                <RotateCw className="text-green-600 mr-3" size={32} />
                <h3 className="text-xl font-bold text-gray-900">Brush Cleaning</h3>
              </div>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compare brush cleaning technology (BIF/RAB) for fibrous organic matter and moderate solids loads. Rotating brushes mechanically scrub screens clean.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View Brush Cleaning
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/self-cleaning"
              className="bg-white border-2 border-blue-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center mb-3">
                <Settings className="text-ctc-blue mr-3" size={32} />
                <h3 className="text-xl font-bold text-gray-900">Self-Cleaning Overview</h3>
              </div>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Detailed comparison of automatic vacuum (AVC) vs automatic brush (RAB) technologies to help you choose the right solution for your application.
              </p>
              <div className="flex items-center text-ctc-blue font-semibold">
                Compare Technologies
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-orange-600 transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-white">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cleaning Options Cross-Links */}
      <section className="py-12 bg-blue-50 border-y border-blue-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Explore All Cleaning Options</h3>
          <p className="text-gray-700 mb-6">
            Vacuum technology (MVC) is available in Semi-Automatic and Fully Automatic configurations for BIG, BIG-S, and GNG series filters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/filtration/inline/cleaning/semi-auto"
              className="bg-ctc-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Semi-Auto (BIF & MVC)
            </Link>
            <Link
              to="/filtration/inline/cleaning/automatic"
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Fully Automatic (RAB & AVC)
            </Link>
            <Link
              to="/filtration/inline/cleaning"
              className="border-2 border-ctc-blue text-ctc-blue px-6 py-3 rounded-lg font-semibold hover:bg-ctc-blue hover:text-white transition-colors"
            >
              Compare All Options
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-blue-200">
            <p className="text-sm text-gray-600">
              <AlertTriangle className="inline mr-2 text-orange-600" size={16} />
              TIG filters do not support Brush/Vacuum/RAB cleaning options.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-orange-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Select Your Vacuum Cleaning System?</h2>
          <p className="text-xl text-orange-100 mb-8">
            Our engineering team will help you choose between manual and automatic vacuum cleaning based on your water quality, solids loading, and site accessibility.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-orange-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to an Engineer
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-orange-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-orange-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My Filter
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-orange-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VacuumCleaningPage;
